import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "header-actions" }
const _hoisted_2 = ["title"]
const _hoisted_3 = {
  key: 0,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "2",
  width: "16",
  height: "16"
}
const _hoisted_4 = {
  key: 1,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "2",
  width: "16",
  height: "16"
}
const _hoisted_5 = { class: "panel-tabs" }
const _hoisted_6 = ["onClick", "title"]
const _hoisted_7 = { class: "tab-label" }
const _hoisted_8 = { class: "panel-content" }
const _hoisted_9 = { class: "panel-footer" }
const _hoisted_10 = { class: "size-indicator" }

import { ref, computed, onMounted, onUnmounted, h } from "vue";
import { useDevPanel, DEV_PANEL_TABS } from "@/composables/useDevPanel";
import { useDevSettings } from "@/composables/useDevSettings";
import DevPanelTrigger from "./DevPanelTrigger.vue";
import ScenariosTab from "./tabs/ScenariosTab.vue";
import StateTab from "./tabs/StateTab.vue";
import EventsTab from "./tabs/EventsTab.vue";
import StorageTab from "./tabs/StorageTab.vue";
import FeatureFlagsTab from "./tabs/FeatureFlagsTab.vue";
import StateHistoryPopover from "./StateHistoryPopover.vue";

type ResizeDirection = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

export default /*@__PURE__*/_defineComponent({
  __name: 'DevPanel',
  setup(__props) {

const {
  isMinimized,
  isExpanded,
  activeTab,
  position,
  height,
  width,
  togglePanel,
  minimizePanel,
  setActiveTab,
  updatePosition,
  updateHeight,
  updateWidth,
  updateSize,
  resetPosition,
  resetSize,
  toggleExpanded,
} = useDevPanel();

const { settings } = useDevSettings();

const tabs = DEV_PANEL_TABS;
const panelRef = ref<HTMLElement | null>(null);

// Count active scenarios
const activeScenarioCount = computed(() => {
  let count = 0;
  if (settings.value.simulateSocketDisconnect) count++;
  if (settings.value.simulatePollingTimeout) count++;
  return count;
});

// Panel position and size style
const panelStyle = computed(() => {
  const pos = position.value;
  const baseHeight = height.value;
  const baseWidth = width.value;
  // When expanded, increase dimensions
  const h = isExpanded.value ? Math.min(baseHeight * 1.5, 900) : baseHeight;
  const w = isExpanded.value ? Math.min(baseWidth * 1.5, 1000) : baseWidth;

  if (pos.x === -1 || pos.y === -1) {
    // Default position: bottom-left
    return {
      left: "20px",
      bottom: "20px",
      height: `${h}px`,
      width: `${w}px`,
    };
  }

  return {
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    height: `${h}px`,
    width: `${w}px`,
  };
});

// Tab icons (inline SVG components)
const getTabIcon = (tabId: string) => {
  const icons: Record<string, any> = {
    scenarios: () =>
      h(
        "svg",
        { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2", width: "16", height: "16" },
        [
          h("path", {
            d: "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z",
          }),
        ],
      ),
    state: () =>
      h(
        "svg",
        { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2", width: "16", height: "16" },
        [h("path", { d: "m18 16 4-4-4-4" }), h("path", { d: "m6 8-4 4 4 4" }), h("path", { d: "m14.5 4-5 16" })],
      ),
    events: () =>
      h(
        "svg",
        { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2", width: "16", height: "16" },
        [
          h("path", { d: "M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" }),
          h("path", { d: "M10.3 21a1.94 1.94 0 0 0 3.4 0" }),
        ],
      ),
    storage: () =>
      h(
        "svg",
        { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2", width: "16", height: "16" },
        [
          h("rect", { width: "20", height: "5", x: "2", y: "3", rx: "1" }),
          h("path", { d: "M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" }),
          h("path", { d: "M10 12h4" }),
        ],
      ),
    flags: () =>
      h(
        "svg",
        { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2", width: "16", height: "16" },
        [
          h("path", { d: "M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" }),
          h("line", { x1: "4", y1: "22", x2: "4", y2: "15" }),
        ],
      ),
  };
  return icons[tabId] || icons.scenarios;
};

// Dragging logic
let isDragging = false;
let dragStartX = 0;
let dragStartY = 0;
let panelStartX = 0;
let panelStartY = 0;

const startDrag = (e: MouseEvent) => {
  if ((e.target as HTMLElement).closest(".header-btn")) return;

  isDragging = true;
  dragStartX = e.clientX;
  dragStartY = e.clientY;

  const panel = panelRef.value;
  if (panel) {
    const rect = panel.getBoundingClientRect();
    panelStartX = rect.left;
    panelStartY = rect.top;
  }

  document.addEventListener("mousemove", onDrag);
  document.addEventListener("mouseup", stopDrag);
};

const onDrag = (e: MouseEvent) => {
  if (!isDragging) return;

  const deltaX = e.clientX - dragStartX;
  const deltaY = e.clientY - dragStartY;

  updatePosition(panelStartX + deltaX, panelStartY + deltaY);
};

const stopDrag = () => {
  isDragging = false;
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", stopDrag);
};

// Resize logic
const isResizingRef = ref(false);
let isResizing = false;
let resizeDirection: ResizeDirection | null = null;
let resizeStartX = 0;
let resizeStartY = 0;
let widthStart = 0;
let heightStart = 0;
let posXStart = 0;
let posYStart = 0;

const handleResizeStart = (e: MouseEvent, direction: ResizeDirection) => {
  isResizing = true;
  isResizingRef.value = true;
  resizeDirection = direction;
  resizeStartX = e.clientX;
  resizeStartY = e.clientY;
  widthStart = width.value;
  heightStart = height.value;
  
  const panel = panelRef.value;
  if (panel) {
    const rect = panel.getBoundingClientRect();
    posXStart = rect.left;
    posYStart = rect.top;
  }

  document.addEventListener("mousemove", onResize);
  document.addEventListener("mouseup", stopResize);
  document.body.style.cursor = getCursorForDirection(direction);
  document.body.style.userSelect = "none";
  document.body.style.webkitUserSelect = "none";
};

const getCursorForDirection = (dir: ResizeDirection): string => {
  const cursors: Record<ResizeDirection, string> = {
    n: "ns-resize",
    s: "ns-resize",
    e: "ew-resize",
    w: "ew-resize",
    ne: "nesw-resize",
    sw: "nesw-resize",
    nw: "nwse-resize",
    se: "nwse-resize",
  };
  return cursors[dir];
};

const onResize = (e: MouseEvent) => {
  if (!isResizing || !resizeDirection) return;
  
  e.preventDefault();

  const deltaX = e.clientX - resizeStartX;
  const deltaY = e.clientY - resizeStartY;
  
  let newWidth = widthStart;
  let newHeight = heightStart;
  let newX = posXStart;
  let newY = posYStart;

  // Handle horizontal resizing
  if (resizeDirection.includes("e")) {
    newWidth = widthStart + deltaX;
  } else if (resizeDirection.includes("w")) {
    newWidth = widthStart - deltaX;
    newX = posXStart + deltaX;
  }

  // Handle vertical resizing
  if (resizeDirection.includes("s")) {
    newHeight = heightStart + deltaY;
  } else if (resizeDirection.includes("n")) {
    newHeight = heightStart - deltaY;
    newY = posYStart + deltaY;
  }

  // Apply constraints
  const minWidth = 320;
  const maxWidth = 1200;
  const minHeight = 200;
  const maxHeight = 900;

  // Clamp width and adjust position if resizing from west
  if (newWidth < minWidth) {
    if (resizeDirection.includes("w")) {
      newX = posXStart + (widthStart - minWidth);
    }
    newWidth = minWidth;
  } else if (newWidth > maxWidth) {
    if (resizeDirection.includes("w")) {
      newX = posXStart + (widthStart - maxWidth);
    }
    newWidth = maxWidth;
  }

  // Clamp height and adjust position if resizing from north
  if (newHeight < minHeight) {
    if (resizeDirection.includes("n")) {
      newY = posYStart + (heightStart - minHeight);
    }
    newHeight = minHeight;
  } else if (newHeight > maxHeight) {
    if (resizeDirection.includes("n")) {
      newY = posYStart + (heightStart - maxHeight);
    }
    newHeight = maxHeight;
  }

  // Update size
  updateSize(newWidth, newHeight);
  
  // Update position if resizing from north or west
  if (resizeDirection.includes("n") || resizeDirection.includes("w")) {
    updatePosition(newX, newY);
  }
};

const stopResize = () => {
  isResizing = false;
  isResizingRef.value = false;
  resizeDirection = null;
  document.removeEventListener("mousemove", onResize);
  document.removeEventListener("mouseup", stopResize);
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
  document.body.style.webkitUserSelect = "";
};

// Keyboard shortcuts
const handleKeyDown = (e: KeyboardEvent) => {
  // Ctrl+Shift+D - Toggle panel
  // Use toUpperCase() because e.key varies with Shift key state
  if (e.ctrlKey && e.shiftKey && e.key.toUpperCase() === "D") {
    e.preventDefault();
    togglePanel();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleKeyDown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeyDown);
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("mousemove", onResize);
  document.removeEventListener("mouseup", stopResize);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (_unref(isMinimized))
      ? (_openBlock(), _createBlock(DevPanelTrigger, {
          key: 0,
          "active-count": activeScenarioCount.value,
          onToggle: _unref(togglePanel)
        }, null, 8, ["active-count", "onToggle"]))
      : _createCommentVNode("", true),
    _createVNode(StateHistoryPopover),
    _createVNode(_Transition, { name: "panel" }, {
      default: _withCtx(() => [
        (!_unref(isMinimized))
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["dev-panel", { expanded: _unref(isExpanded), resizing: isResizingRef.value }]),
              style: _normalizeStyle(panelStyle.value),
              ref_key: "panelRef",
              ref: panelRef
            }, [
              _createElementVNode("div", {
                class: "panel-header",
                onMousedown: startDrag
              }, [
                _cache[17] || (_cache[17] = _createElementVNode("div", { class: "header-left" }, [
                  _createElementVNode("span", { class: "logo" }, [
                    _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      fill: "none",
                      stroke: "currentColor",
                      "stroke-width": "2",
                      width: "18",
                      height: "18"
                    }, [
                      _createElementVNode("path", { d: "M12 2L2 7l10 5 10-5-10-5Z" }),
                      _createElementVNode("path", { d: "m2 17 10 5 10-5" }),
                      _createElementVNode("path", { d: "m2 12 10 5 10-5" })
                    ])
                  ]),
                  _createElementVNode("span", { class: "title" }, "Dev Panel"),
                  _createElementVNode("span", { class: "env-badge" }, "LOCAL")
                ], -1)),
                _createElementVNode("div", _hoisted_1, [
                  _createElementVNode("button", {
                    class: "header-btn",
                    onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(toggleExpanded) && _unref(toggleExpanded)(...args))),
                    title: _unref(isExpanded) ? 'Collapse' : 'Expand'
                  }, [
                    (!_unref(isExpanded))
                      ? (_openBlock(), _createElementBlock("svg", _hoisted_3, [...(_cache[12] || (_cache[12] = [
                          _createElementVNode("path", { d: "M15 3h6v6" }, null, -1),
                          _createElementVNode("path", { d: "M9 21H3v-6" }, null, -1),
                          _createElementVNode("path", { d: "M21 3l-7 7" }, null, -1),
                          _createElementVNode("path", { d: "M3 21l7-7" }, null, -1)
                        ]))]))
                      : (_openBlock(), _createElementBlock("svg", _hoisted_4, [...(_cache[13] || (_cache[13] = [
                          _createElementVNode("path", { d: "M4 14h6v6" }, null, -1),
                          _createElementVNode("path", { d: "M20 10h-6V4" }, null, -1),
                          _createElementVNode("path", { d: "M14 10l7-7" }, null, -1),
                          _createElementVNode("path", { d: "M3 21l7-7" }, null, -1)
                        ]))]))
                  ], 8, _hoisted_2),
                  _createElementVNode("button", {
                    class: "header-btn",
                    onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(resetSize) && _unref(resetSize)(...args))),
                    title: "Reset Size"
                  }, [...(_cache[14] || (_cache[14] = [
                    _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      fill: "none",
                      stroke: "currentColor",
                      "stroke-width": "2",
                      width: "16",
                      height: "16"
                    }, [
                      _createElementVNode("rect", {
                        x: "3",
                        y: "3",
                        width: "18",
                        height: "18",
                        rx: "2"
                      }),
                      _createElementVNode("path", { d: "M9 3v18" }),
                      _createElementVNode("path", { d: "M3 9h18" })
                    ], -1)
                  ]))]),
                  _createElementVNode("button", {
                    class: "header-btn",
                    onClick: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(resetPosition) && _unref(resetPosition)(...args))),
                    title: "Reset Position"
                  }, [...(_cache[15] || (_cache[15] = [
                    _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      fill: "none",
                      stroke: "currentColor",
                      "stroke-width": "2",
                      width: "16",
                      height: "16"
                    }, [
                      _createElementVNode("path", { d: "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" }),
                      _createElementVNode("path", { d: "M3 3v5h5" })
                    ], -1)
                  ]))]),
                  _createElementVNode("button", {
                    class: "header-btn",
                    onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(minimizePanel) && _unref(minimizePanel)(...args))),
                    title: "Minimize (Ctrl+Shift+D)"
                  }, [...(_cache[16] || (_cache[16] = [
                    _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      fill: "none",
                      stroke: "currentColor",
                      "stroke-width": "2",
                      width: "16",
                      height: "16"
                    }, [
                      _createElementVNode("path", { d: "M5 12h14" })
                    ], -1)
                  ]))])
                ])
              ], 32),
              _createElementVNode("div", _hoisted_5, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(tabs), (tab) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: tab.id,
                    class: _normalizeClass(["tab-btn", { active: _unref(activeTab) === tab.id }]),
                    onClick: ($event: any) => (_unref(setActiveTab)(tab.id)),
                    title: tab.label
                  }, [
                    (_openBlock(), _createBlock(_resolveDynamicComponent(getTabIcon(tab.id)))),
                    _createElementVNode("span", _hoisted_7, _toDisplayString(tab.label), 1)
                  ], 10, _hoisted_6))
                }), 128))
              ]),
              _createElementVNode("div", _hoisted_8, [
                (_unref(activeTab) === 'scenarios')
                  ? (_openBlock(), _createBlock(ScenariosTab, { key: 0 }))
                  : (_unref(activeTab) === 'state')
                    ? (_openBlock(), _createBlock(StateTab, { key: 1 }))
                    : (_unref(activeTab) === 'events')
                      ? (_openBlock(), _createBlock(EventsTab, { key: 2 }))
                      : (_unref(activeTab) === 'storage')
                        ? (_openBlock(), _createBlock(StorageTab, { key: 3 }))
                        : (_unref(activeTab) === 'flags')
                          ? (_openBlock(), _createBlock(FeatureFlagsTab, { key: 4 }))
                          : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_9, [
                _cache[18] || (_cache[18] = _createElementVNode("span", { class: "shortcut-hint" }, [
                  _createElementVNode("kbd", null, "Ctrl"),
                  _createTextVNode("+"),
                  _createElementVNode("kbd", null, "Shift"),
                  _createTextVNode("+"),
                  _createElementVNode("kbd", null, "D"),
                  _createTextVNode(" to toggle ")
                ], -1)),
                _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(width)) + "×" + _toDisplayString(_unref(height)), 1)
              ]),
              _createElementVNode("div", {
                class: "resize-handle resize-n",
                onMousedown: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (handleResizeStart($event, 'n')), ["prevent","stop"]))
              }, null, 32),
              _createElementVNode("div", {
                class: "resize-handle resize-s",
                onMousedown: _cache[5] || (_cache[5] = _withModifiers(($event: any) => (handleResizeStart($event, 's')), ["prevent","stop"]))
              }, null, 32),
              _createElementVNode("div", {
                class: "resize-handle resize-e",
                onMousedown: _cache[6] || (_cache[6] = _withModifiers(($event: any) => (handleResizeStart($event, 'e')), ["prevent","stop"]))
              }, null, 32),
              _createElementVNode("div", {
                class: "resize-handle resize-w",
                onMousedown: _cache[7] || (_cache[7] = _withModifiers(($event: any) => (handleResizeStart($event, 'w')), ["prevent","stop"]))
              }, null, 32),
              _createElementVNode("div", {
                class: "resize-handle resize-ne",
                onMousedown: _cache[8] || (_cache[8] = _withModifiers(($event: any) => (handleResizeStart($event, 'ne')), ["prevent","stop"]))
              }, null, 32),
              _createElementVNode("div", {
                class: "resize-handle resize-nw",
                onMousedown: _cache[9] || (_cache[9] = _withModifiers(($event: any) => (handleResizeStart($event, 'nw')), ["prevent","stop"]))
              }, null, 32),
              _createElementVNode("div", {
                class: "resize-handle resize-se",
                onMousedown: _cache[10] || (_cache[10] = _withModifiers(($event: any) => (handleResizeStart($event, 'se')), ["prevent","stop"]))
              }, null, 32),
              _createElementVNode("div", {
                class: "resize-handle resize-sw",
                onMousedown: _cache[11] || (_cache[11] = _withModifiers(($event: any) => (handleResizeStart($event, 'sw')), ["prevent","stop"]))
              }, null, 32)
            ], 6))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})